/**
 * `<NoWorkspaceRoute>` — `/no-workspace`
 *
 * Per `aidlc-docs/construction/auth-workspaces-rbac/functional-design/frontend-components.md § <NoWorkspacePage>`.
 *
 * Reached when an authenticated user has zero memberships (e.g., their
 * only workspace was deleted via webhook). Offers two actions:
 *
 *   1. Create a new workspace inline (small form: name → POST /api/workspaces
 *      → navigate to `/w/<slug>`). This is the only entry point for
 *      workspace creation while the user has no shell to host the
 *      switcher's "Create new workspace" item (which CG-6 owns).
 *   2. Sign out — clears the session and returns the user to `/login`.
 *
 * Validation reuses the canonical `createWorkspaceSchema` from
 * `@cometchat-ai/auth/shared`.
 */

import { authClient, type AuthClientError, useAuth } from "@cometchat-ai/auth/client";
import { createWorkspaceSchema, type CreateWorkspaceDto } from "@cometchat-ai/auth/shared";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "../components/icons";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";

import { SlugPreview } from "../components/slug-preview";
import { AuthLayout } from "../layouts/auth-layout";
import { formatApiError } from "../lib/format-error";

export function NoWorkspaceRoute() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();

  const form = useForm<CreateWorkspaceDto>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: { name: "", logoUrl: "" },
  });

  const create = useMutation({
    mutationFn: (values: CreateWorkspaceDto) => authClient.createWorkspace(values),
    onSuccess: async (result) => {
      // Re-fetch /api/me so the auth-provider picks up the new
      // workspace-scoped JWT that the server just minted (ENG-35588
      // Flow F). Without this, <RequireAuth> would still see
      // `workspace === null` and bounce back to /no-workspace.
      await auth.refresh();
      void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      navigate(`/w/${result.workspace.slug}`);
    },
    onError: (err: AuthClientError) => {
      toast.error(t("noWorkspace.createFailed"), { description: formatApiError(err) });
    },
  });

  const signOut = useMutation({
    mutationFn: () => authClient.logout(),
    onSuccess: () => {
      void queryClient.clear();
      navigate("/login");
    },
    onError: (err: AuthClientError) => {
      toast.error(t("noWorkspace.signOutFailed"), { description: formatApiError(err) });
    },
  });

  // A member with no workspace can't create one (server `assertCanCreateWorkspace`
  // would 403). Show an explanatory state instead of a form that dead-ends.
  // `canCreateWorkspace` is server-resolved (reliable workspace-less, where the
  // JWT carries no permissions).
  if (!auth.canCreateWorkspace) {
    return (
      <AuthLayout
        title={t("noWorkspace.cannotCreateTitle")}
        subtitle={t("noWorkspace.cannotCreateBody")}
      >
        <div className="space-y-6" data-testid="no-workspace-cannot-create">
          <div className="space-y-2 text-center text-sm text-muted-foreground">
            {auth.user ? <p data-testid="no-workspace-signed-in-as">{auth.user.email}</p> : null}
            <Button
              type="button"
              variant="ghost"
              className="h-10 w-full rounded-xl"
              disabled={signOut.isPending}
              onClick={() => signOut.mutate()}
              data-testid="no-workspace-signout"
            >
              {signOut.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {t("noWorkspace.signOut")}
            </Button>
          </div>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={t("noWorkspace.title")} subtitle={t("noWorkspace.description")}>
      <div className="space-y-6" data-testid="no-workspace-body">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit((values) => create.mutate(values))}
            className="space-y-4"
            data-testid="no-workspace-create-form"
            noValidate
          >
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("noWorkspace.nameLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      autoComplete="organization"
                      placeholder="Acme Co"
                      data-testid="no-workspace-name"
                      className="h-12 rounded-xl text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                      {...field}
                    />
                  </FormControl>
                  <SlugPreview from={field.value} />
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="logoUrl"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("noWorkspace.iconLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      type="url"
                      inputMode="url"
                      autoComplete="off"
                      placeholder={t("noWorkspace.iconPlaceholder")}
                      data-testid="no-workspace-icon"
                      className="h-12 rounded-xl text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                      {...field}
                      value={field.value ?? ""}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="h-10 w-full rounded-xl hover:bg-primary-hover"
              disabled={create.isPending}
              data-testid="no-workspace-create"
            >
              {create.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {t("noWorkspace.createCta")}
            </Button>
          </form>
        </Form>

        <Separator />

        <div className="space-y-2 text-center text-sm text-muted-foreground">
          {auth.user ? <p data-testid="no-workspace-signed-in-as">{auth.user.email}</p> : null}
          <Button
            type="button"
            variant="ghost"
            className="h-10 w-full rounded-xl"
            disabled={signOut.isPending}
            onClick={() => signOut.mutate()}
            data-testid="no-workspace-signout"
          >
            {signOut.isPending && (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
            )}
            {t("noWorkspace.signOut")}
          </Button>
        </div>
      </div>
    </AuthLayout>
  );
}
